import { useQuery } from '@apollo/client';
import {
	CampaignOrderByColumn,
	IdFilter,
	SpotifyDashboardStatsDocument,
	SpotifyDashboardStatsLifetimeDocument,
} from 'wb-spotify/graphql/generated';
import { DATE_PARSE_FORMAT, useUrlParams } from '@aleph/shared-components';
import { useEffect, useMemo, useState } from 'react';
import { CellPosition } from 'ag-grid-community';
import { SPOTIFY_SUPERGRID_STATISTICS_DIMENSIONS } from 'wb-spotify/app/routes/superGrid/statistics/utils';
import { useNavigate, useParams } from 'react-router-dom';
import useSupergridSettings from 'wb-spotify/app/routes/superGrid/hooks/useSupergridSettings';
import dayjs from 'dayjs';
import { getDateRangeForSupergridFromParams } from 'wb-spotify/app/routes/superGrid/utils';

type UseDashboardStatsParams = {
	parentFilter: Record<string, [IdFilter]>,
	focusedCell: CellPosition | null,
	selectedKPI?: string | null,
	skip?: boolean,
};

const useDashboardStats = ({
	parentFilter,
	focusedCell,
	selectedKPI,
	skip = false,
}: UseDashboardStatsParams) => {
	const navigate = useNavigate();
	const { params } = useUrlParams({ navigate, useParams });

	const [cachedData, setCachedData] = useState<any>();
	const { lifetimeStats } = useSupergridSettings();

	const focusedCellColumn = useMemo(() => {
		const cell = focusedCell?.column.getColId();

		if (focusedCell?.column.getColId()) {
			const metric = SPOTIFY_SUPERGRID_STATISTICS_DIMENSIONS.find(m => m === cell);

			if (metric) {
				return metric as CampaignOrderByColumn;
			}
		}

		return (selectedKPI ?? 'listeners') as CampaignOrderByColumn;
	}, [focusedCell, selectedKPI]);

	const paramsDate = getDateRangeForSupergridFromParams(params);
	const dateFrom = dayjs(paramsDate?.split(':')?.[0]);
	const dateTo = dayjs(paramsDate?.split(':')?.[1]);
	const dateDiff = dateTo.diff(dateFrom, 'days') + 1;

	const lastDate = `${dateFrom.subtract(dateDiff, 'days').format(DATE_PARSE_FORMAT)}:${dateTo.subtract(dateDiff, 'days').format(DATE_PARSE_FORMAT)}`;

	const {
		data: dashboardStats, loading,
	} = useQuery(
		lifetimeStats
			? SpotifyDashboardStatsLifetimeDocument
			: SpotifyDashboardStatsDocument,
		{
			variables: {
				filters: {
					...params.filters,
					...parentFilter,
					// Remove date filter to get lifetime stats
					date: lifetimeStats ? undefined : {
						value: paramsDate,
					},
				},
				performerFilters:     parentFilter,
				currDateFilter:       { value: paramsDate },
				lastDateFilter:       { value: lastDate },
				performersSortColumn: focusedCellColumn,
			},
			returnPartialData: false,
			skip,
		},
	);

	useEffect(() => {
		if (dashboardStats && !loading) {
			setCachedData(dashboardStats);
		}
	}, [dashboardStats]);

	return {
		reloading:       loading,
		loading:         loading && !cachedData,
		stats:           cachedData?.sparklineStats?.items ?? [],
		bestPerformers:  cachedData?.bestPerformers?.items ?? [],
		worstPerformers: cachedData?.worstPerformers?.items ?? [],
	};
};

export default useDashboardStats;
